import React, { ReactNode, useCallback, useState } from "react";
import {
  Divider,
  Stack,
  Typography,
  TableContainer,
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableRow,
  TableCell,
  Box,
  styled,
  TableBody,
  TableFooter,
  TablePagination,
  useTheme,
  IconButton,
  Button
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { Checkbox } from "./forms";

interface THeader {
  label: string;
  key: string;
  orderStuts?: string;
  onOrderChange?: () => void;
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = useCallback(
    (event: any) => {
      onPageChange(event, 0);
    },
    [onPageChange]
  );

  const handleBackButtonClick = useCallback(
    (event: any) => {
      onPageChange(event, page - 1);
    },
    [onPageChange, page]
  );

  const handleNextButtonClick = useCallback(
    (event: any) => {
      onPageChange(event, page + 1);
    },
    [onPageChange, page]
  );

  const handleLastPageButtonClick = useCallback(
    (event: any) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    },
    [onPageChange, count, rowsPerPage]
  );

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const RowItem = styled(TableRow)({
  cursor: "pointer",
  ":hover": {
    backgroundColor: "#F6F6F6"
  }
});

const Table = ({
  headers,
  rows = [],
  checkable = false,
  emptyRowMessage,
  rowClick,
  checkedActions,
  currPage,
  totalCount,
  rowsPerPage,
  totalPageCount,
  handleChangePage,
  handleChangeRowsPerPage
}: {
  headers: THeader[];
  rows: { [key in string]: ReactNode }[];
  checkable?: boolean;
  emptyRowMessage?: ReactNode;
  rowClick?: (item: any) => void;
  checkedActions?: {
    label: string;
    action: (items: { [key in string]: ReactNode }[]) => void;
  }[];
  currPage?: number;
  totalCount?: number;
  rowsPerPage?: number;
  totalPageCount?: number;
  handleChangePage?: (event: any, newPage: any) => void;
  handleChangeRowsPerPage?: (event: any) => void;
}) => {
  const [checkedItems, setCheckedItems] = useState<
    { [key in string]: ReactNode }[]
  >([]);

  const handleHeaderCheckItem = useCallback(() => {
    setCheckedItems(curr => {
      if (curr.length > 0) {
        return [];
      } else {
        return [...rows];
      }
    });
  }, [rows]);

  const handleCheckItem = useCallback((v: { [key in string]: ReactNode }) => {
    setCheckedItems(curr => {
      if (curr.includes(v)) {
        return curr.filter(item => item !== v);
      } else {
        return [...curr, v];
      }
    });
  }, []);

  const handleCheckedItems = useCallback(
    (e: any) => {
      e(checkedItems);
      setCheckedItems([]);
    },
    [checkedItems]
  );

  return (
    <Box>
      <Stack
        gap="8px"
        direction="row-reverse"
        alignItems="center"
        justifyContent="space-between"
        my={2}
      >
        <Typography variant="h6">{rows.length}건</Typography>
        {checkable && checkedActions && (
          <Stack gap="8px" direction="row">
            {checkedActions?.map((item, idx) => (
              <Button
                key={idx}
                onClick={() => handleCheckedItems(item.action)}
                variant="outlined"
                size="small"
                color="inherit"
                disableRipple
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
      <Divider />
      <TableContainer>
        <MuiTable
          aria-label="custom pagination table"
          sx={{
            whiteSpace: "nowrap"
          }}
        >
          <MuiTableHead>
            <TableRow>
              {checkable && (
                <TableCell sx={{ width: "50px" }}>
                  <Checkbox
                    checked={
                      rows.length > 0 && checkedItems.length === rows.length
                    }
                    onChange={handleHeaderCheckItem}
                  />
                </TableCell>
              )}

              {headers.map((header, idx) => (
                <TableCell
                  key={idx}
                  style={{
                    textAlign: "center",
                    ...(header.onOrderChange && { cursor: "pointer" })
                  }}
                  onClick={header.onOrderChange}
                >
                  <Typography variant="h6">
                    {header.label}{" "}
                    {header.orderStuts &&
                      (header.orderStuts === "0" ? (
                        <KeyboardArrowUp />
                      ) : (
                        <KeyboardArrowDown />
                      ))}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </MuiTableHead>

          <TableBody>
            {rows.length < 1 ? (
              <TableRow>
                <TableCell
                  colSpan={checkable ? headers.length + 1 : headers.length}
                  align="center"
                >
                  {emptyRowMessage || "내용이 없습니다."}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {rows.map((row, row_idx) => {
                  const cellColor = row.cellColor as string;
                  return (
                    <RowItem key={row_idx}>
                      {checkable && (
                        <TableCell>
                          <Checkbox
                            checked={checkedItems.includes(row)}
                            onChange={() => handleCheckItem(row)}
                          />
                        </TableCell>
                      )}
                      {headers.map((item, item_idx) => (
                        <TableCell
                          key={item_idx}
                          align="center"
                          onClick={() => rowClick && rowClick(row)}
                          sx={{ backgroundColor: cellColor }}
                        >
                          <Typography variant="subtitle2">
                            {row[item.key]}
                          </Typography>
                        </TableCell>
                      ))}
                    </RowItem>
                  );
                })}
              </>
            )}
          </TableBody>
          <TableFooter>
            {currPage && totalCount && rowsPerPage && totalPageCount && (
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 30, 40, 50]}
                  colSpan={checkable ? headers.length + 1 : headers.length}
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={currPage - 1}
                  SelectProps={{
                    native: true
                  }}
                  onPageChange={
                    handleChangePage
                      ? handleChangePage
                      : () => console.log("aa")
                  }
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  labelDisplayedRows={({ from, to, count }) => {
                    return `현재 ${currPage} 페이지 / 총 ${totalPageCount} 페이지`;
                  }}
                  labelRowsPerPage="페이지당 게시글 수"
                />
              </TableRow>
            )}
          </TableFooter>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

export default Table;
